import { useEffect, useState, type ReactNode } from 'react';
import { t } from '@bhorosha/i18n';

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="bh-center">
      <div className="bh-spinner" aria-hidden />
      <div className="bh-muted">{label ?? t('common.loading')}</div>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="bh-field">
      <span className="bh-field-label">{label}</span>
      {children}
      {hint && <span className="bh-field-hint">{hint}</span>}
    </label>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <div className="bh-error" role="alert">{children}</div>;
}

export function Notice({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'ok' | 'warn' }) {
  return <div className={`bh-notice bh-notice-${tone}`}>{children}</div>;
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div className="bh-overlay no-print" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bh-modal${wide ? ' bh-modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="bh-modal-head">
          <h2>{title}</h2>
          <button className="bh-icon-btn" onClick={onClose} aria-label={t('common.close')}>&times;</button>
        </div>
        <div className="bh-modal-body">{children}</div>
      </div>
    </div>
  );
}

const FEATURES: { key: string; icon: ReactNode }[] = [
  { key: 'auth.feat.buy', icon: <path d="M5 3h14a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm2 3v3h10V6H7zm0 6h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v6h-2v-6zm-8 4h2v2H7v-2zm4 0h2v2h-2v-2z" /> },
  { key: 'auth.feat.remind', icon: <path d="M12 2a6 6 0 0 0-6 6v3.6L4.3 15A1 1 0 0 0 5.2 16.5h13.6a1 1 0 0 0 .9-1.5L18 11.6V8a6 6 0 0 0-6-6zm-2.5 16a2.5 2.5 0 0 0 5 0h-5z" /> },
  { key: 'auth.feat.visits', icon: <path d="M6 2v2H5v5a5 5 0 0 0 4 4.9V15a5 5 0 0 0 10 0v-1.2a2.5 2.5 0 1 0-2 0V15a3 3 0 0 1-6 0v-1.1A5 5 0 0 0 15 9V4h-1V2h-2v4h1v3a3 3 0 0 1-6 0V6h1V2H6z" /> },
  { key: 'auth.feat.family', icon: <path d="M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm8 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8 13c-3.3 0-6 1.8-6 4v2h12v-2c0-2.2-2.7-4-6-4zm8 0c-.7 0-1.4.1-2 .3 1.2.9 2 2.2 2 3.7v2h6v-2c0-2.2-2.7-4-6-4z" /> },
];

/** Split layout used by sign-in, sign-up, invites and first-time setup. */
export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="bh-auth-page">
      <div className="bh-auth-shell">
        <aside className="bh-auth-brand" aria-hidden="false">
          <div className="bh-brand">
            <BrandMark size={48} />
            <span className="bh-brand-name">{t('app.name')}</span>
          </div>
          <p className="bh-brand-tag">{t('app.tagline')}</p>
          <ul className="bh-brand-feats">
            {FEATURES.map(f => (
              <li key={f.key}><svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">{f.icon}</svg>{t(f.key)}</li>
            ))}
          </ul>
          <p className="bh-brand-quote">{t('auth.meaning')}</p>
        </aside>
        <main className="bh-auth-card">{children}</main>
      </div>
    </div>
  );
}

export function initials(name: string) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** The Bhorosha logo: the same colourful pill as the browser-tab icon. */
export function BrandMark({ size = 40 }: { size?: number }) {
  return (
    <span className="bh-brand-mark" style={{ width: size, height: size, fontSize: Math.round(size * 0.62) }} role="img" aria-label="Bhorosha">
      <span aria-hidden="true">💊</span>
    </span>
  );
}

// ---------- password strength ----------
const COMMON = ['password', 'passw0rd', '12345678', '123456789', '1234567890', 'qwerty', 'qwertyuiop', 'abc12345',
  'iloveyou', 'admin123', 'welcome1', 'bhorosha', 'letmein', '11111111', '00000000', 'asdfghjk'];

export type Strength = { score: 0 | 1 | 2 | 3 | 4; label: string; tip: string };

/** 0 = too short, 1 = weak, 2 = fair, 3 = good, 4 = strong. */
export function passwordStrength(pw: string): Strength {
  if (!pw) return { score: 0, label: '', tip: '' };
  if (pw.length < 8) return { score: 0, label: t('pw.tooShort'), tip: t('pw.tipLength') };
  const lower = pw.toLowerCase();
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length;
  let points = 1;
  if (pw.length >= 12) points++;
  if (pw.length >= 16) points++;
  if (classes >= 2) points++;
  if (classes >= 3) points++;
  if (classes === 4) points++;
  const weakPattern = COMMON.some(c => lower.includes(c)) || /^(.)\1+$/.test(pw) || /(0123|1234|2345|3456|4567|5678|6789|abcd|bcde|qwer|asdf)/i.test(pw);
  if (weakPattern) points = Math.min(points, 1);
  const score = (points <= 2 ? 1 : points === 3 ? 2 : points === 4 ? 3 : 4) as Strength['score'];
  const tip = weakPattern ? t('pw.tipCommon') : score < 3 ? (pw.length < 12 ? t('pw.tipLonger') : t('pw.tipMix')) : '';
  return { score, label: t(['pw.tooShort', 'pw.weak', 'pw.fair', 'pw.good', 'pw.strong'][score]), tip };
}

export function PasswordStrength({ value }: { value: string }) {
  const s = passwordStrength(value);
  if (!value) return null;
  const level = Math.max(1, s.score);
  return (
    <span className={`bh-strength bh-strength-${s.score}`} aria-live="polite">
      <span className="bh-strength-bar" aria-hidden="true">
        {[1, 2, 3, 4].map(i => <span key={i} className={i <= level ? 'on' : ''} />)}
      </span>
      <span className="bh-strength-text"><b>{s.label}</b>{s.tip ? ` · ${s.tip}` : ''}</span>
    </span>
  );
}

/** Password box with a show / hide eye, and an optional strong / weak bar underneath. */
export function PasswordInput({ value, onChange, autoComplete = 'new-password', autoFocus, strength, matchWith, ariaLabel }: {
  value: string; onChange: (v: string) => void; autoComplete?: string; autoFocus?: boolean;
  /** show the strength bar */ strength?: boolean;
  /** for "type it again" boxes: show whether it matches */ matchWith?: string;
  ariaLabel?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <>
      <span className="bh-pw">
        <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} aria-label={ariaLabel}
          autoComplete={autoComplete} required minLength={autoComplete === 'new-password' ? 8 : undefined} autoFocus={autoFocus} />
        <button type="button" className="bh-pw-eye" onClick={() => setShow(v => !v)} aria-label={show ? t('auth.hidePw') : t('auth.showPw')}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />{show && <path d="M3 3l18 18" />}
          </svg>
        </button>
      </span>
      {strength && <PasswordStrength value={value} />}
      {matchWith !== undefined && value && (
        <span className={`bh-match ${value === matchWith ? 'ok' : 'no'}`}>{value === matchWith ? t('pw.match') : t('pw.noMatch')}</span>
      )}
    </>
  );
}
